/**
 * EG Search (Meilisearch) – variant selectors.
 *
 * Renders variant pickers inside search result cards and keeps the selected variant in sync.
 */
window.EGSearchVariants = (function () {
  'use strict';

  const FIXED_MAPPED_PRODUCT_ID_BY_HANDLE_FALLBACK = Object.freeze({
    '3-cube-pot': 8500111900868,
  });
  const VIRTUAL_BUNDLE_HANDLE_ALLOWLIST_FALLBACK = new Set([
    'epic-tray-6-cell-germination-dome-and-bottom-tray',
    'epic-tray-4-cell-germination-dome-and-bottom-tray',
    'ultimate-beginner-seed-starting-bundle',
    '3-cube-pot',
    'epic-16-cell-seed-starting-trays',
    '29-tall-birdies-large-4pack',
    '15-short-birdies-large-4pack',
    '29-tall-birdies-medium-4pack',
    '15-short-birdies-medium-4pack',
    '29-tall-birdies-small-4pack',
    '15-short-birdies-small-4pack',
  ]);

  const germinationRelatedBundlesCache = new Map();
  const mappedProductByIdCache = new Map();
  const mappedVariantFromPdpCache = new Map();
  let storefrontAccessTokenCache = undefined;
  function setCardMappedVariantOverride(card, variant) {
    if (!card) return;
    if (!variant || !variant.id) {
      delete card.dataset.useMappedVariant;
      delete card.dataset.mappedVariantId;
      delete card.dataset.mappedVariantAvailable;
      return;
    }

    card.dataset.useMappedVariant = 'true';
    card.dataset.mappedVariantId = String(variant.id);
    card.dataset.mappedVariantAvailable = variant.available ? 'true' : 'false';
  }

  function getCardMappedVariantOverride(card) {
    if (!card || card.dataset.useMappedVariant !== 'true') return null;

    const id = Number(card.dataset.mappedVariantId);
    if (!Number.isFinite(id) || id <= 0) return null;

    return {
      id,
      available: card.dataset.mappedVariantAvailable === 'true'
    };
  }

  function getFixedMappedTargetProductIdByHandle(handle) {
    const normalizedHandle = String(handle || '').trim();
    if (!normalizedHandle) return null;

    if (typeof window.EGInventoryOverrides?.getFixedMappedTargetProductId === 'function') {
      return window.EGInventoryOverrides.getFixedMappedTargetProductId(normalizedHandle);
    }

    return FIXED_MAPPED_PRODUCT_ID_BY_HANDLE_FALLBACK[normalizedHandle] || null;
  }

  function shouldApplyVirtualBundleOverwriteByHandle(handle) {
    const normalizedHandle = String(handle || '').trim();
    if (!normalizedHandle) return false;

    if (typeof window.EGInventoryOverrides?.shouldApplyVirtualBundleOverwrite === 'function') {
      return window.EGInventoryOverrides.shouldApplyVirtualBundleOverwrite(normalizedHandle);
    }

    return VIRTUAL_BUNDLE_HANDLE_ALLOWLIST_FALLBACK.has(normalizedHandle);
  }

  function isGerminationHandle(handle) {
    const h = String(handle || '').trim();
    return h === 'epic-tray-6-cell-germination-dome-and-bottom-tray'
      || h === 'epic-tray-4-cell-germination-dome-and-bottom-tray';
  }

  function desiredGerminationSizeFromHandle(handle) {
    const h = String(handle || '').toLowerCase();
    if (h.includes('6-cell') || h.includes('6 cell')) return '6';
    if (h.includes('4-cell') || h.includes('4 cell')) return '4';
    return null;
  }

  function pickBundleVariantFromRelatedBundles(relatedBundles, desiredSize) {
    if (!Array.isArray(relatedBundles) || relatedBundles.length === 0) return null;

    let mappedBundle = null;
    for (const bundle of relatedBundles) {
      const bt = String(bundle?.title || '').toLowerCase();
      const bh = String(bundle?.handle || '').toLowerCase();
      const sizeMatch = desiredSize === '6'
        ? (bt.includes('6-cell') || bt.includes('6 cell') || bh.includes('6-cell') || bh.includes('6 cell'))
        : (bt.includes('4-cell') || bt.includes('4 cell') || bh.includes('4-cell') || bh.includes('4 cell'));
      if (sizeMatch) {
        mappedBundle = bundle;
        break;
      }
    }

    if (!mappedBundle) mappedBundle = relatedBundles[0];
    const variants = Array.isArray(mappedBundle?.variants) ? mappedBundle.variants : [];
    if (!variants.length) return null;
    return variants.find((v) => !!v?.available) || variants[0] || null;
  }

  async function fetchGerminationRelatedBundlesByHandle(handle) {
    const normalizedHandle = String(handle || '').trim();
    if (!normalizedHandle) return null;
    if (germinationRelatedBundlesCache.has(normalizedHandle)) {
      return germinationRelatedBundlesCache.get(normalizedHandle);
    }

    const request = fetch(`/products/${normalizedHandle}`)
      .then((response) => (response.ok ? response.text() : null))
      .then((html) => {
        if (!html) return null;
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const script = doc.querySelector('#gcb-product-variants');
        if (!script) return null;
        const text = (script.textContent || '').trim();
        if (!text) return null;
        const parsed = JSON.parse(text);
        return Array.isArray(parsed) ? parsed : null;
      })
      .catch(() => null);

    germinationRelatedBundlesCache.set(normalizedHandle, request);
    return request;
  }

  function getStorefrontAccessToken() {
    if (storefrontAccessTokenCache !== undefined) return storefrontAccessTokenCache;
    storefrontAccessTokenCache = null;

    try {
      const featuresEl = document.getElementById('shopify-features');
      if (!featuresEl) return storefrontAccessTokenCache;
      const parsed = JSON.parse(featuresEl.textContent || '{}');
      const token = String(parsed?.accessToken || '').trim();
      if (token) storefrontAccessTokenCache = token;
    } catch (_) {
      storefrontAccessTokenCache = null;
    }

    return storefrontAccessTokenCache;
  }

  function parseNumericIdFromGid(gid) {
    const raw = String(gid || '').trim();
    if (!raw) return null;
    const idPart = raw.includes('/') ? raw.split('/').pop() : raw;
    const id = Number(idPart);
    return Number.isFinite(id) && id > 0 ? id : null;
  }

  async function fetchMappedProductById(productId) {
    const normalizedId = Number(productId);
    if (!Number.isFinite(normalizedId) || normalizedId <= 0) return null;

    if (mappedProductByIdCache.has(normalizedId)) {
      return mappedProductByIdCache.get(normalizedId);
    }

    const request = (async () => {
      const storefrontToken = getStorefrontAccessToken();
      if (!storefrontToken) return null;

      const response = await fetch('/api/2025-10/graphql.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': storefrontToken
        },
        body: JSON.stringify({
          query: `
            query MappedProduct($id: ID!) {
              product(id: $id) {
                id
                handle
                variants(first: 50) {
                  nodes {
                    id
                    availableForSale
                    selectedOptions {
                      value
                    }
                  }
                }
              }
            }
          `,
          variables: {
            id: `gid://shopify/Product/${normalizedId}`
          }
        })
      });

      if (!response.ok) return null;
      const payload = await response.json();
      const product = payload?.data?.product;
      if (!product) return null;

      const variants = Array.isArray(product?.variants?.nodes) ? product.variants.nodes : [];
      const mappedVariants = variants
        .map((variant) => {
          const numericVariantId = parseNumericIdFromGid(variant?.id);
          if (!numericVariantId) return null;

          const selectedOptions = Array.isArray(variant?.selectedOptions) ? variant.selectedOptions : [];
          return {
            id: numericVariantId,
            available: !!variant?.availableForSale,
            option1: selectedOptions[0]?.value || null,
            option2: selectedOptions[1]?.value || null,
            option3: selectedOptions[2]?.value || null,
          };
        })
        .filter(Boolean);

      return {
        id: normalizedId,
        handle: product?.handle || '',
        variants: mappedVariants
      };
    })().catch(() => null);

    mappedProductByIdCache.set(normalizedId, request);
    return request;
  }

  function buildProductPageUrl(handle) {
    const normalizedHandle = String(handle || '').trim();
    if (!normalizedHandle) return null;

    const root = window.Shopify?.routes?.root || '/';
    const url = new URL(`${root}products/${normalizedHandle}`, window.location.origin);
    const currentParams = new URLSearchParams(window.location.search || '');
    ['preview_theme_id', '_fd', 'pb'].forEach((key) => {
      if (currentParams.has(key)) {
        url.searchParams.set(key, currentParams.get(key));
      }
    });
    return url.toString();
  }

  async function fetchMappedVariantFromProductPageForm(handle) {
    const normalizedHandle = String(handle || '').trim();
    if (!normalizedHandle) return null;

    if (mappedVariantFromPdpCache.has(normalizedHandle)) {
      return mappedVariantFromPdpCache.get(normalizedHandle);
    }

    const request = (async () => {
      const url = buildProductPageUrl(normalizedHandle);
      if (!url) return null;

      const response = await fetch(url);
      if (!response.ok) return null;

      const html = await response.text();
      if (!html) return null;

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      const variantInput =
        doc.querySelector('form[id^="product-form-"] input.product-variant-id[name="id"]') ||
        doc.querySelector('form[action="/cart/add"] input.product-variant-id[name="id"]');

      if (!variantInput) return null;

      const variantId = Number(variantInput.value || variantInput.getAttribute('value'));
      if (!Number.isFinite(variantId) || variantId <= 0) return null;

      const isDisabled = variantInput.hasAttribute('disabled');
      return {
        id: variantId,
        available: !isDisabled
      };
    })().catch(() => null);

    mappedVariantFromPdpCache.set(normalizedHandle, request);
    return request;
  }

  function pickMappedVariantFromProduct(mappedProduct, baseVariant) {
    const mappedVariants = Array.isArray(mappedProduct?.variants) ? mappedProduct.variants : [];
    if (!mappedVariants.length) return null;

    if (baseVariant) {
      const byOptions = mappedVariants.find((variant) => {
        for (let idx = 1; idx <= 3; idx += 1) {
          const key = `option${idx}`;
          const baseValue = baseVariant?.[key];
          const mappedValue = variant?.[key];
          if (baseValue == null || baseValue === '') continue;
          if (mappedValue !== baseValue) return false;
        }
        return true;
      });
      if (byOptions) return byOptions;
    }

    return mappedVariants.find((variant) => !!variant?.available) || mappedVariants[0] || null;
  }

  function getDynamicVirtualBundleVariants(card) {
    if (!card || card.dataset.isVirtualBundle !== 'true') return [];
    try {
      const variants = JSON.parse(card.dataset.virtualBundleVariants || '[]');
      return Array.isArray(variants) ? variants : [];
    } catch (_) {
      return [];
    }
  }

  function getDynamicVirtualBundleVariant(card, baseVariant) {
    const variants = getDynamicVirtualBundleVariants(card);
    if (!variants.length) return null;
    return pickMappedVariantFromProduct({ variants }, baseVariant);
  }

  // Color name -> hex (used for swatches).
  const COLOR_MAP = {
    // Primary colors
    'white': '#ffffff',
    'black': '#000000',
    'red': '#dc143c',
    'blue': '#4169e1',
    'yellow': '#ffd700',
    'green': '#2e8b57',
    'orange': '#ff8c00',
    'purple': '#9370db',
    'pink': '#ff69b4',
    'brown': '#8b4513',
    'gray': '#808080',
    'grey': '#808080',

    // Spanish color names
    'azul': '#4169e1',
    'verde': '#2e8b57',
    'rojo': '#dc143c',
    'amarillo': '#ffd700',
    'naranja': '#ff8c00',
    'blanco': '#ffffff',
    'negro': '#000000',
    'rosa': '#ff69b4',
    'morado': '#9370db',
    'marron': '#8b4513',
    'gris': '#808080',

    // Extended blues
    'navy': '#000080',
    'teal': '#008080',
    'sky blue': '#87ceeb',
    'midnight blue': '#191970',
    'deep ocean': '#1a4b6e',
    'aqua': '#00ffff',
    'turquoise': '#40e0d0',
    'cobalt': '#0047ab',
    'royal blue': '#4169e1',
    'steel blue': '#4682b4',
    'powder blue': '#b0e0e6',
    'light blue': '#add8e6',
    'dark blue': '#00008b',

    // Extended greens
    'forest green': '#228b22',
    'mist green': '#c4d4c0',
    'olive': '#808000',
    'sage': '#9dc183',
    'moss': '#8a9a5b',
    'fern': '#4f7942',
    'jade': '#00a86b',
    'lime': '#32cd32',
    'mint': '#98ff98',
    'hunter green': '#355e3b',
    'dark green': '#006400',
    'light green': '#90ee90',
    'emerald': '#50c878',
    'seafoam': '#93e9be',
    'kelly green': '#4cbb17',

    // Extended browns/neutrals
    'beige': '#f5f5dc',
    'bone': '#e8e0d5',
    'paperbark': '#d4c4b0',
    'natural': '#c4b99e',
    'sand': '#c2b280',
    'cream': '#fffdd0',
    'terracotta': '#e2725b',
    'rust': '#b7410e',
    'tan': '#d2b48c',
    'khaki': '#c3b091',
    'camel': '#c19a6b',
    'sienna': '#a0522d',
    'umber': '#635147',
    'chocolate': '#7b3f00',
    'coffee': '#6f4e37',
    'espresso': '#3c2415',
    'taupe': '#483c32',
    'mocha': '#967969',
    'walnut': '#773f1a',
    'chestnut': '#954535',
    'mahogany': '#c04000',

    // Extended grays
    'slate grey': '#5a6a6e',
    'slate gray': '#5a6a6e',
    'slate': '#708090',
    'charcoal': '#36454f',
    'graphite': '#383838',
    'pewter': '#8e9091',
    'silver': '#c0c0c0',
    'ash': '#b2beb5',
    'smoke': '#738276',
    'stone': '#928e85',
    'dark gray': '#a9a9a9',
    'dark grey': '#a9a9a9',
    'light gray': '#d3d3d3',
    'light grey': '#d3d3d3',
    'gunmetal': '#2a3439',

    // Extended reds/pinks
    'coral': '#ff7f50',
    'salmon': '#fa8072',
    'crimson': '#dc143c',
    'burgundy': '#800020',
    'maroon': '#800000',
    'rose': '#ff007f',
    'blush': '#de5d83',
    'magenta': '#ff00ff',
    'fuchsia': '#ff00ff',
    'berry': '#8e4585',
    'wine': '#722f37',
    'raspberry': '#e30b5c',
    'cherry': '#de3163',

    // Extended oranges/yellows
    'peach': '#ffcba4',
    'apricot': '#fbceb1',
    'tangerine': '#ff9966',
    'amber': '#ffbf00',
    'gold': '#ffd700',
    'mustard': '#ffdb58',
    'sunflower': '#ffda03',
    'honey': '#eb9605',
    'marigold': '#eaa221',
    'butterscotch': '#e29d4e',
    'pumpkin': '#ff7518',

    // Extended purples
    'lavender': '#e6e6fa',
    'violet': '#ee82ee',
    'plum': '#dda0dd',
    'lilac': '#c8a2c8',
    'mauve': '#e0b0ff',
    'grape': '#6f2da8',
    'eggplant': '#614051',
    'aubergine': '#614051',
    'indigo': '#4b0082',
    'orchid': '#da70d6',
    'amethyst': '#9966cc',

    // Multi-word variations
    'forest': '#228b22',
    'ocean': '#1a4b6e',
    'sky': '#87ceeb',
    'midnight': '#191970',
    'hunter': '#355e3b',
  };

  function getColorHex(colorName) {
    if (!colorName) return null;
    const normalized = colorName.toLowerCase().trim();
    return COLOR_MAP[normalized] || null;
  }

  function isColorOption(optionName) {
    const colorKeywords = ['color', 'colour', 'colors', 'colours'];
    return colorKeywords.some(keyword => optionName.toLowerCase().includes(keyword));
  }

  function isRaisedBedProductForHiddenColors(product, hit) {
    const values = [
      product?.type,
      product?.product_type,
      hit?.product_type,
      hit?.type
    ].map(value => String(value || '').toLowerCase());

    if (values.some(value => value.includes('raised garden bed') || value.includes('raised beds'))) {
      return true;
    }

    const raisedBedHeight = product?.raised_bed_height || hit?.raised_bed_height;
    if (Array.isArray(raisedBedHeight) ? raisedBedHeight.length > 0 : !!raisedBedHeight) {
      return true;
    }

    const handle = String(product?.handle || hit?.handle || '').toLowerCase();
    return handle.includes('raised-garden-bed') || handle.includes('metal-raised-garden-bed');
  }

  function isVariantInStock(variant) {
    if (!variant) return false;
    return variant.inventory_management !== 'shopify' || Number(variant.inventory_quantity) > 0;
  }

  function getPreferredInitialVariant(product, hit, preferInStock) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const hitVariant = variants.find((variant) =>
      String(variant.id) === String(hit?.variant_id || hit?.objectID)
    );

    if (preferInStock) {
      const discountedInStock = variants.find((variant) =>
        isVariantInStock(variant) && variant.compare_at_price && variant.compare_at_price > variant.price
      );
      const firstInStock = discountedInStock || variants.find(isVariantInStock);
      if (firstInStock && !isVariantInStock(hitVariant)) return firstInStock;
    }

    const discountedAvailable = variants.find((variant) =>
      variant.available && variant.compare_at_price && variant.compare_at_price > variant.price
    );
    return hitVariant || discountedAvailable || variants.find((variant) => variant.available) || variants[0] || null;
  }

  function updateCardDiscountBadge(card, hasDiscount, discountPercent) {
    if (!card) return;

    let badge = card.querySelector('.ais-hit--discount-badge');
    if (!hasDiscount || !Number.isFinite(discountPercent) || discountPercent <= 0) {
      if (badge) {
        const row = badge.closest('.ais-hit--badge-row');
        badge.remove();
        if (row && row.children.length === 0) row.remove();
      }
      return;
    }

    const picture = card.querySelector('.ais-hit--picture-container');
    if (!picture) return;

    let badgeRow = picture.querySelector('.ais-hit--badge-row');
    if (!badgeRow) {
      badgeRow = document.createElement('div');
      badgeRow.className = 'ais-hit--badge-row';
      picture.prepend(badgeRow);
    }

    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'badge badge--top-right ais-hit--discount-badge';
      // Keep discount first/left-most in the row.
      badgeRow.prepend(badge);
    } else if (badge.parentElement !== badgeRow) {
      badgeRow.prepend(badge);
    } else if (badgeRow.firstElementChild !== badge) {
      badgeRow.prepend(badge);
    }

    badge.textContent = `${discountPercent}% off`;
  }

  function formatUSDFromCents(cents) {
    const n = Number(cents);
    if (!Number.isFinite(n) || n < 0) return '$0.00';
    return `$${(n / 100).toFixed(2)}`;
  }

  function priceDataAttrs(productId, variantId) {
    const productAttr = productId ? ` data-product-id="${String(productId).replace(/"/g, '&quot;')}"` : '';
    const variantAttr = variantId ? ` data-variant-id="${String(variantId).replace(/"/g, '&quot;')}"` : '';
    return `${productAttr}${variantAttr}`;
  }

  function refreshIntelligemsPrices() {
    if (typeof window.igData?.resetDom === 'function') window.igData.resetDom();
  }

  function renderMemberMessageMarkup(basePriceCents, hasSale, memberEligible, handle, variantId) {
    if (!memberEligible) return '';

    const isMember = window.EPIC_GARDENING?.isGreenhouseMember === true;
    if (isMember) {
      return '';
    }

    const memberPriceCents = Math.ceil(basePriceCents * 0.9);
    return `
      <p class="member-savings member-savings-clp member-savings-search" data-card-member-state="guest">
        <a href="/products/${handle}?variant=${variantId}&member-price=membership" class="member-savings__link">Member Price <span class="member-savings-amount">${formatUSDFromCents(memberPriceCents)}</span></a>
      </p>
    `;
  }

  function findCardByHandle(handle) {
    if (!handle) return null;
    const cards = document.querySelectorAll('.ais-hit[data-handle]');
    for (const card of cards) {
      if ((card.dataset.handle || '') === String(handle)) return card;
    }
    return null;
  }

  function updateCardPriceAndBadgeFromVariant(card, variant) {
    if (!card || !variant) return;
    updateCardProductLinks(card, variant.id);
    window.EGSearchResultsRender?.refreshPackQtyDropdown?.(card, variant.price);
    const priceEl = card.querySelector('.ais-hit--price');
    if (priceEl) {
      const memberEligible = card.dataset.memberEligible !== 'false';
      const isMember = window.EPIC_GARDENING?.isGreenhouseMember === true;
      const hasDiscount = variant.compare_at_price && variant.compare_at_price > variant.price;
      const showMemberPrice = isMember && memberEligible;
      const discountPercent = hasDiscount
        ? Math.round(((variant.compare_at_price - variant.price) * 100) / variant.compare_at_price)
        : 0;
      const priceAttrs = priceDataAttrs(card.dataset.productId, variant.id);

      priceEl.className = `ais-hit--price ${(hasDiscount || showMemberPrice) ? 'has-discount' : ''}`;
      if (showMemberPrice) {
        const strikePriceCents = hasDiscount ? Number(variant.compare_at_price) : Number(variant.price);
        const memberPriceCents = Math.ceil(Number(variant.price) * 0.9);
        priceEl.innerHTML = `
          <span class="igComparePrice"${priceAttrs}>${formatUSDFromCents(strikePriceCents)}</span>
          <span class="igPrice"${priceAttrs}>${formatUSDFromCents(memberPriceCents)}</span>
        `;
      } else {
        priceEl.innerHTML = hasDiscount
          ? `<span class="igComparePrice"${priceAttrs}>${formatUSDFromCents(variant.compare_at_price)}</span>
             <span class="igPrice"${priceAttrs}>${formatUSDFromCents(variant.price)}</span>`
          : `<span class="igPrice"${priceAttrs}>${formatUSDFromCents(variant.price)}</span>`;
      }

      const memberMessageWrap = card.querySelector('.ais-hit--member-message');
      if (memberMessageWrap) {
        memberMessageWrap.innerHTML = renderMemberMessageMarkup(
          Number(variant.price) || 0,
          hasDiscount,
          memberEligible,
          card.dataset.handle || '',
          variant.id
        );
      }

      updateCardDiscountBadge(card, hasDiscount, discountPercent);
      refreshIntelligemsPrices();
    }
  }

  function updateCardAddToCartState(card, variant) {
    if (!card || !variant) return;
    card.dataset.selectedVariantId = variant.id;
    delete card.dataset.awaitingMappedInventory;

    const button = card.querySelector('.ais-hit--add-to-cart');
    if (!button) return;
    button.dataset.variantId = variant.id;
    delete button.dataset.awaitingMappedInventory;
    const variantInput = card.querySelector('.ais-hit--variant-input, .product-variant-id');
    if (variantInput) {
      variantInput.value = variant.id;
      variantInput.setAttribute('value', variant.id);
      variantInput.disabled = false;
      const mappedVariant = getCardMappedVariantOverride(card);
      if (mappedVariant && String(mappedVariant.id) === String(variant.id)) {
        variantInput.dataset.forceMappedVariantId = String(mappedVariant.id);
        variantInput.dataset.forceMappedAvailable = mappedVariant.available ? 'true' : 'false';
      } else {
        delete variantInput.dataset.forceMappedVariantId;
        delete variantInput.dataset.forceMappedAvailable;
      }
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const isPurpleDotManaged = button.closest('form[data-type="add-to-cart-form"]')?.classList.contains('x-pd-atc-form');
    if (isPurpleDotManaged) return;

    // Match collection-card behavior: keep spans/spinner intact and toggle sold-out message.
    let addTextSpan = button.querySelector('span:not(.sold-out-message)');
    let soldOutSpan = button.querySelector('.sold-out-message');

    // Repair DOM if a previous render replaced markup (e.g. via textContent)
    if (!addTextSpan) {
      addTextSpan = document.createElement('span');
      addTextSpan.textContent = (button.textContent || '').trim() || 'Add to cart';
      button.prepend(addTextSpan);
    }

    if (!soldOutSpan) {
      soldOutSpan = document.createElement('span');
      soldOutSpan.className = 'sold-out-message hidden';
      soldOutSpan.textContent = 'Sold out';
      addTextSpan.insertAdjacentElement('afterend', soldOutSpan);
    }

    if (variant.available) {
      button.disabled = false;
      button.removeAttribute('aria-disabled');
      addTextSpan.classList.remove('hidden');
      soldOutSpan.classList.add('hidden');
      let preorderState = { active: false, timeline: '' };
      try {
        const states = JSON.parse(card.dataset.preorderVariants || '{}');
        preorderState = states[String(variant.id)] || preorderState;
      } catch (_) {}
      addTextSpan.textContent = preorderState.active
        ? 'Pre-Order'
        : 'Add to cart';
    } else {
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      addTextSpan.classList.add('hidden');
      soldOutSpan.classList.remove('hidden');
      soldOutSpan.textContent = 'Sold out';
    }

    let preorderState = { active: false, timeline: '' };
    try {
      const states = JSON.parse(card.dataset.preorderVariants || '{}');
      preorderState = states[String(variant.id)] || preorderState;
    } catch (_) {}
    const preorderTimeline = card.querySelector('.ais-hit--preorder-timeline');
    if (preorderTimeline) {
      preorderTimeline.hidden = !preorderState.active;
      if (preorderState.timeline) preorderTimeline.textContent = preorderState.timeline;
    }
  }

  let cardObserver;
  const scheduledCards = new Map();

  function resetScheduledCards() {
    cardObserver?.disconnect();
    scheduledCards.forEach((start, card) => {
      card.removeEventListener('pointerenter', start);
      card.removeEventListener('focusin', start);
    });
    scheduledCards.clear();
  }

  function scheduleCards(hits, root, startIndex = 0) {
    if (!cardObserver && 'IntersectionObserver' in window) {
      cardObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) scheduledCards.get(entry.target)?.();
        });
      }, { rootMargin: '600px 0px' });
    }
    const cards = Array.from(root.querySelectorAll('.ais-hit'));
    hits.forEach((hit, index) => {
      const card = cards.find((item) => item.dataset.handle === hit.handle);
      if (!card || card.dataset.egHydration) return;
      card.dataset.egHydration = 'pending';
      const buttons = Array.from(card.querySelectorAll('button.ais-hit--button'));
      const disabledStates = buttons.map((button) => button.disabled);
      buttons.forEach((button) => { button.disabled = true; });
      card.setAttribute('aria-busy', 'true');
      const start = () => {
        cardObserver?.unobserve(card);
        scheduledCards.delete(card);
        card.removeEventListener('pointerenter', start);
        card.removeEventListener('focusin', start);
        if (!card.isConnected || card.dataset.egHydration !== 'pending') return;
        card.dataset.egHydration = 'loading';
        window.EGSearchResultsRender?.triggerOkendoReviews(card);
        fetchAndRenderVariants(hit, card).then((success) => {
          card.dataset.egHydration = success ? 'complete' : 'error';
          card.removeAttribute('aria-busy');
          // Preserve the existing index/metadata fallback if Shopify cannot be reached.
          if (!success) buttons.forEach((button, i) => { button.disabled = disabledStates[i]; });
        });
      };
      scheduledCards.set(card, start);
      card.addEventListener('pointerenter', start);
      card.addEventListener('focusin', start);
      if (startIndex + index < 4 || !cardObserver || window.Shopify?.designMode) start();
      else cardObserver.observe(card);
    });
  }

  // Keep the existing price, inventory and preorder logic when a card is hydrated.
  function fetchAndRenderVariants(hit, expectedCard) {
    const cardId = hit.objectID || hit.variant_id;
    const container = document.getElementById(`variant-options-${cardId}`);
    const card = (container && container.closest('.ais-hit')) || findCardByHandle(hit.handle);
    if (!card || (expectedCard && card !== expectedCard)) return Promise.resolve();
    let inventoryResolved = false;

    return fetch(`${window.Shopify?.routes?.root || '/'}products/${hit.handle}.js`)
      .then(response => {
        if (!response.ok) throw new Error(`Product request failed: ${response.status}`);
        return response.json();
      })
      .then(async (product) => {
        if (!card.isConnected) return;
        const hideUnavailableColorValues = isRaisedBedProductForHiddenColors(product, hit);
        const selectedVariant = getPreferredInitialVariant(product, hit, hideUnavailableColorValues);
        if (!selectedVariant) return false;

        let mappedVariantOverride = null;
        const handle = String(hit?.handle || product?.handle || '').trim();
        const dynamicBundleVariant = getDynamicVirtualBundleVariant(card, selectedVariant || null);
        const shouldApplyMappedOverride = !!dynamicBundleVariant || shouldApplyVirtualBundleOverwriteByHandle(handle);
        if (shouldApplyMappedOverride) {
          if (dynamicBundleVariant) {
            mappedVariantOverride = dynamicBundleVariant;
          } else if (isGerminationHandle(handle)) {
            const relatedBundles = await fetchGerminationRelatedBundlesByHandle(handle);
            const desiredSize = desiredGerminationSizeFromHandle(handle);
            mappedVariantOverride = pickBundleVariantFromRelatedBundles(relatedBundles, desiredSize);
          }

          if (!mappedVariantOverride) {
            const fixedMappedProductId = getFixedMappedTargetProductIdByHandle(handle);
            if (fixedMappedProductId) {
              // Fetch mapped product directly by fixed Product ID first.
              const mappedProduct = await fetchMappedProductById(fixedMappedProductId);
              mappedVariantOverride = pickMappedVariantFromProduct(mappedProduct, selectedVariant || null);
            }
          }

          if (!mappedVariantOverride) {
            // Fall back to the active PDP form variant from page HTML.
            mappedVariantOverride = await fetchMappedVariantFromProductPageForm(handle);
          }
        }

        if (!card.isConnected) return;
        if (selectedVariant) {
          updateCardPriceAndBadgeFromVariant(card, selectedVariant);
          if (mappedVariantOverride) {
            setCardMappedVariantOverride(card, mappedVariantOverride);
            updateCardAddToCartState(card, mappedVariantOverride);
          } else {
            setCardMappedVariantOverride(card, null);
            updateCardAddToCartState(card, selectedVariant);
          }
          inventoryResolved = true;
        }

        // Variant selectors are only needed when there are selectable options.
        if (!container) return true;
        if (typeof renderAlgoliaVariantSelectors === 'function') {
          renderAlgoliaVariantSelectors(product, cardId, hit.id, false, selectedVariant?.id);
        } else {
          renderMeiliVariantSelectors(product, cardId, hit.id, hideUnavailableColorValues, selectedVariant?.id);
        }
        return true;
      })
      .catch(error => {
        console.error('Error fetching product variants:', error);
        // A picker rendering error must not replace freshly resolved stock with the index fallback.
        return inventoryResolved;
      });
  }

  function updateCardProductLinks(card, variantId) {
    card.querySelectorAll('a[href*="/products/"]').forEach((link) => {
      const url = new URL(link.getAttribute('href'), window.location.origin);
      url.searchParams.set('variant', String(variantId));
      link.setAttribute('href', `${url.pathname}${url.search}${url.hash}`);
    });
  }

  // Render variant selectors (Meili search cards)
  function renderMeiliVariantSelectors(product, cardId, productId, hideUnavailableColorValues = false, selectedVariantId = null) {
    const container = document.getElementById(`variant-options-${cardId}`);
    if (!container || !product.options || product.options.length === 0) return;

    const card = container.closest('.ais-hit');
    const escapeHtml = window.EGSearchConfig.escapeHtml;
    container.innerHTML = '';

    const displayOptions = product.options.filter(opt => opt.name !== 'Title');
    if (displayOptions.length === 0) return;

  const handle = (product && product.handle) ? String(product.handle) : '';
  const is1020Tray = handle === '1020-universal-bottom-tray';
  const is16Cell = handle === 'epic-16-cell-seed-starting-trays' || handle === 'epic-16-cell-seed-starting-trays-pack';
  const isGermination = handle === 'epic-tray-6-cell-germination-dome-and-bottom-tray' || handle === 'epic-tray-4-cell-germination-dome-and-bottom-tray';

    const firstAvailable = product.variants.find((variant) => String(variant.id) === String(selectedVariantId))
      || getPreferredInitialVariant(product, null, hideUnavailableColorValues);

    displayOptions.forEach((option) => {
    // For these "special" PDP products, we show a separate pack/tray quantity dropdown on the search card.
    // Hide the pack-size option dropdown to avoid duplicate/confusing selectors.
    if (is1020Tray && option.name === 'Quantity') return;
    if (is16Cell && option.name === 'Quantity') return;
    if (isGermination && (option.name === 'Size' || option.name === 'Quantity')) return;

      if (option.values.length <= 1) return;

      const defaultValue = firstAvailable ? firstAvailable[`option${option.position}`] : option.values[0];
      const isColor = isColorOption(option.name);

      const optionDiv = document.createElement('div');
      optionDiv.className = 'card-variant-option';

      if (isColor) {
        const visibleValues = hideUnavailableColorValues
          ? option.values.filter(value => product.variants.some(v =>
            v[`option${option.position}`] === value && v.available
          ))
          : option.values;

        if (visibleValues.length === 0) return;

        optionDiv.innerHTML = `
          <label class="card-variant-label">
            ${escapeHtml(option.name)}: <span class="selected-value">${escapeHtml(defaultValue)}</span>
          </label>
          <div class="card-variant-options color-options" data-option-position="${option.position}">
            ${visibleValues.map(value => {
          const isAvailable = product.variants.some(v =>
            v[`option${option.position}`] === value && v.available
          );
          const isDefault = value === defaultValue;
          const colorHex = getColorHex(value);
          const pillClass = 'pill-' + value.toLowerCase().replace(/\s+/g, '-');
          const colorStyle = colorHex ? `background-color: ${colorHex};` : '';
          return `
                <label class="color-swatch-label" title="${escapeHtml(value)}">
                  <input type="radio" 
                         name="color-${cardId}-option${option.position}" 
                         class="card-variant-radio color-radio"
                         value="${escapeHtml(value)}"
                         data-product-id="${productId}"
                         data-option-position="${option.position}"
                         ${isDefault ? 'checked' : ''}
                         ${!isAvailable ? 'disabled' : ''}
                         onclick="event.stopPropagation();" />
                  <span class="color-pill ${pillClass} ${!isAvailable ? 'disabled' : ''}" 
                        style="${colorStyle}"
                        data-color="${escapeHtml(value)}"></span>
                </label>
              `;
        }).join('')}
          </div>
        `;
      } else {
        optionDiv.innerHTML = `
          <label class="card-variant-label" for="select-${cardId}-option${option.position}">
            ${escapeHtml(option.name)}:
          </label>
          <div class="card-variant-select-wrapper">
            <select id="select-${cardId}-option${option.position}"
                    class="card-variant-select"
                    data-product-id="${productId}"
                    data-option-position="${option.position}"
                    onclick="event.stopPropagation();">
              ${option.values.map(value => {
          const isAvailable = product.variants.some(v =>
            v[`option${option.position}`] === value && v.available
          );
          const isDefault = value === defaultValue;
          return `<option value="${escapeHtml(value)}" ${isDefault ? 'selected' : ''} ${!isAvailable ? 'disabled' : ''}>
                        ${escapeHtml(value)}${!isAvailable ? ' - Unavailable' : ''}
                      </option>`;
        }).join('')}
            </select>
          </div>
        `;
      }

      container.appendChild(optionDiv);
    });

    // Change listeners
    container.querySelectorAll('.card-variant-select').forEach(select => {
      select.addEventListener('change', () => {
        updateMeiliSelectedVariant(product, card, productId);
      });
    });

    container.querySelectorAll('.color-radio').forEach(radio => {
      radio.addEventListener('change', (e) => {
        const optionContainer = e.target.closest('.card-variant-option');
        const selectedValueSpan = optionContainer.querySelector('.selected-value');
        if (selectedValueSpan) {
          selectedValueSpan.textContent = e.target.value;
        }
        updateMeiliSelectedVariant(product, card, productId);
      });
    });

    card.dataset.variants = JSON.stringify(product.variants);
    updateMeiliSelectedVariant(product, card, productId);
  }

  // Update the selected variant and refresh UI bits (image/price/button state)
  function updateMeiliSelectedVariant(product, card, productId) {
    const selectedOptions = [];

    card.querySelectorAll('.card-variant-select').forEach(select => {
      const position = parseInt(select.dataset.optionPosition) - 1;
      selectedOptions[position] = select.value;
    });

    card.querySelectorAll('.color-radio:checked').forEach(radio => {
      const position = parseInt(radio.dataset.optionPosition) - 1;
      selectedOptions[position] = radio.value;
    });

    const matchingVariant = product.variants.find(variant => {
      return selectedOptions.every((value, index) => {
        if (!value) return true;
        return variant[`option${index + 1}`] === value;
      });
    });

    card.dataset.invalidVariantSelection = String(!matchingVariant);
    card.querySelectorAll('.ais-hit--price, .ais-hit--discount-badge, .member-savings')
      .forEach((element) => element.classList.toggle('hidden', !matchingVariant));

    if (!matchingVariant) {
      delete card.dataset.selectedVariantId;
      const button = card.querySelector('.ais-hit--add-to-cart');
      if (button) {
        delete button.dataset.variantId;
        button.disabled = true;
        button.setAttribute('aria-disabled', 'true');
        const label = button.querySelector('span:not(.sold-out-message)');
        if (label) {
          label.textContent = 'Unavailable';
          label.classList.remove('hidden');
        }
        button.querySelector('.sold-out-message')?.classList.add('hidden');
      }
      const input = card.querySelector('.ais-hit--variant-input, .product-variant-id');
      if (input) {
        input.value = '';
        input.disabled = true;
      }
      return;
    }

    if (matchingVariant) {
      card.dataset.selectedVariantId = matchingVariant.id;

      if (matchingVariant.featured_image) {
        const imgElement = card.querySelector('.ais-hit--picture');
        if (imgElement) {
          const newSrc = window.EGSearchResultsRender.resultImageUrl(matchingVariant.featured_image.src
            .replace(/_(pico|icon|thumb|small|compact|medium|large|grande|original|1024x1024|2048x2048|master)\./g, '.'));
          imgElement.srcset = window.EGSearchResultsRender.resultImageSrcset(newSrc);
          imgElement.src = newSrc;
        }
      }

      updateCardPriceAndBadgeFromVariant(card, matchingVariant);
      const dynamicBundleVariant = getDynamicVirtualBundleVariant(card, matchingVariant);
      if (dynamicBundleVariant) setCardMappedVariantOverride(card, dynamicBundleVariant);
      const mappedVariantOverride = dynamicBundleVariant || getCardMappedVariantOverride(card);
      if (mappedVariantOverride) {
        updateCardAddToCartState(card, mappedVariantOverride);
      } else {
        updateCardAddToCartState(card, matchingVariant);
      }
    }
  }

  return {
    scheduleCards,
    resetScheduledCards,
    fetchAndRenderVariants,
    renderMeiliVariantSelectors,
    updateMeiliSelectedVariant,
    getColorHex,
    isColorOption
  };
})();
